import React, { useState, useEffect } from 'react';
import { setDoc, doc, getDoc, collection, query, where, limit, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import PhoneInput from '../components/ui/PhoneInput';
import SmsComplianceCheckboxes from '../components/ui/SmsComplianceCheckboxes';
import SEO from '../components/shared/SEO';

export function PlannerWebsiteRenderer({ sections, theme, planner, onInquire }) {
    const globalBg = theme.backgroundColor || '#ffffff';
    const radius = theme.borderRadius || '0.5rem';

    const renderSection = (section) => {
        if (!section.enabled) return null;
        const { type, content, id } = section;

        const sectionStyle = {
            backgroundColor: content.bgColor || globalBg,
            color: content.textColor || (globalBg === '#111827' ? '#ffffff' : '#1f2937'),
            padding: content.padding || '4rem 1rem'
        };

        switch (type) {
            case 'hero':
                return (
                    <div key={id} style={{ ...sectionStyle, minHeight: content.height || '100vh', display: 'flex', alignItems: content.verticalAlign || 'center', justifyContent: content.horizontalAlign === 'left' ? 'flex-start' : content.horizontalAlign === 'right' ? 'flex-end' : 'center', position: 'relative', overflow: 'hidden' }}>
                        {content.imageUrl && (
                            <>
                                <img src={content.imageUrl} alt="Hero" className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black" style={{ opacity: content.overlayOpacity !== undefined ? content.overlayOpacity : 0.4 }}></div>
                            </>
                        )}
                        <div className={`relative z-10 w-full max-w-4xl p-8 ${content.horizontalAlign === 'left' ? 'text-left' : content.horizontalAlign === 'right' ? 'text-right' : 'text-center'}`}>
                            <h1 className="text-5xl md:text-7xl font-bold mb-4 font-serif" style={{ color: content.textColor || theme.primaryColor }}>{content.title}</h1>
                            <p className="text-xl md:text-2xl mb-8" style={{ color: content.textColor || (globalBg === '#111827' ? '#e5e7eb' : '#4b5563') }}>{content.subtitle}</p>
                            {content.buttonText && (
                                <button onClick={() => {
                                    if (content.buttonAction === 'chat') {
                                        onInquire && onInquire();
                                    } else {
                                        const el = document.getElementById('contact-section');
                                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                                    }
                                }} className="px-8 py-3 font-bold text-white transition hover:opacity-90 shadow-lg" style={{ backgroundColor: theme.primaryColor, borderRadius: radius }}>
                                    {content.buttonText}
                                </button>
                            )}
                        </div>
                    </div>
                );
            case 'about':
                return (
                    <div key={id} style={sectionStyle} className="border-b border-gray-100/10">
                        <div className="container mx-auto max-w-6xl">
                            <div className={`flex items-center gap-12 flex-col md:flex-row ${content.layout === 'right' ? 'md:flex-row-reverse' : ''}`}>
                                {content.imageUrl && (
                                    <div className="w-full md:w-1/2">
                                        <div className="relative">
                                            <div className="absolute inset-0 transform translate-x-4 translate-y-4" style={{ backgroundColor: theme.primaryColor, opacity: 0.2, borderRadius: radius }}></div>
                                            <img src={content.imageUrl} alt="About" className="relative shadow-xl w-full h-auto object-cover aspect-[4/5] md:aspect-square" style={{ borderRadius: radius }} />
                                        </div>
                                    </div>
                                )}
                                <div className={`w-full ${content.imageUrl ? 'md:w-1/2' : ''} space-y-6`}>
                                    <h2 className="text-4xl font-bold font-serif" style={{ color: theme.primaryColor }}>{content.heading}</h2>
                                    <div className="prose prose-lg leading-relaxed whitespace-pre-wrap" style={{ color: content.textColor || '#374151' }}>
                                        {content.text}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'services':
                return (
                    <div key={id} style={sectionStyle} className="border-b border-gray-100/10">
                        <div className="container mx-auto max-w-7xl">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold font-serif mb-4" style={{ color: theme.primaryColor }}>{content.heading || 'Our Services'}</h2>
                                {content.subtitle && <p className="text-lg opacity-80 max-w-2xl mx-auto">{content.subtitle}</p>}
                            </div>
                            <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                                {(content.packages || []).map((pkg, idx) => (
                                    <div key={idx} className="bg-white shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow flex flex-col" style={{ borderRadius: radius }}>
                                        {pkg.imageUrl && <img src={pkg.imageUrl} alt={pkg.title} className="w-full h-56 object-cover" />}
                                        <div className="p-8 flex-1 flex flex-col">
                                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.title}</h3>
                                            <p className="text-2xl font-bold mb-4" style={{ color: theme.primaryColor }}>
                                                {pkg.price ? `$${pkg.price}` : 'Custom Quote'}
                                            </p>
                                            <p className="text-gray-600 mb-6 flex-1">{pkg.description}</p>
                                            <ul className="space-y-3 mb-8">
                                                {(pkg.features || []).map((feature, i) => (
                                                    <li key={i} className="flex items-start text-sm text-gray-700">
                                                        <svg className="w-5 h-5 mr-2 flex-shrink-0" style={{ color: theme.primaryColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button onClick={() => onInquire && onInquire(pkg.title)} className="mt-auto text-center w-full py-3 font-bold transition text-white hover:opacity-90 shadow-md" style={{ backgroundColor: theme.primaryColor, borderRadius: radius }}>
                                                Inquire Now
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'gallery':
                return (
                    <div key={id} style={sectionStyle}>
                        <div className="container mx-auto max-w-7xl">
                            <h2 className="text-4xl font-bold text-center font-serif mb-12" style={{ color: theme.primaryColor }}>{content.heading || 'Portfolio'}</h2>
                            <div className="gap-4 space-y-4 columns-1 sm:columns-2 lg:columns-3">
                                {(content.images || []).map((img, idx) => (
                                    <div key={idx} className="break-inside-avoid overflow-hidden group relative shadow-sm" style={{ borderRadius: radius }}>
                                        <img src={img.url} alt={`Portfolio ${idx}`} className="w-full h-auto object-cover transform group-hover:scale-105 transition duration-500" />
                                        {img.caption && (
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center p-4">
                                                <p className="text-white text-center font-bold text-lg">{img.caption}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'contact':
                return (
                    <div key={id} id="contact-section" style={{ ...sectionStyle, backgroundColor: content.bgColor || '#1f2937', color: content.textColor || '#ffffff' }}>
                        <div className="container mx-auto max-w-4xl text-center">
                            <h2 className="text-4xl font-bold font-serif mb-6">{content.heading || "Let's Plan Your Perfect Day"}</h2>
                            <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto">{content.text}</p>
                            <div className="flex items-center gap-6 flex-col sm:flex-row justify-center">
                                {planner?.email && (
                                    <a href={`mailto:${planner.email}`} className="flex items-center gap-3 text-lg font-medium hover:opacity-80 transition">
                                        <span className="p-3 bg-white/10" style={{ borderRadius: radius }}>✉️</span> {planner.email}
                                    </a>
                                )}
                                {planner?.phone && (
                                    <a href={`tel:${planner.phone}`} className="flex items-center gap-3 text-lg font-medium hover:opacity-80 transition">
                                        <span className="p-3 bg-white/10" style={{ borderRadius: radius }}>📞</span> {planner.phone}
                                    </a>
                                )}
                            </div>
                            <div className="mt-12 pt-12 border-t border-white/20">
                                <p className="opacity-60 text-sm">Powered by Host-it! Pro</p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ fontFamily: theme.fontFamily, backgroundColor: globalBg }} className="min-h-full transition-colors duration-300">
            {sections.map(section => renderSection(section))}
        </div>
    );
}

export default function PlannerWebsitePublicView({ plannerId, db, navigateTo, user }) {
    const [planner, setPlanner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState('');
    const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', eventDate: '', message: '' });
    const [submittingLead, setSubmittingLead] = useState(false);
    const [leadSuccess, setLeadSuccess] = useState(false);

    useEffect(() => {
        if (!plannerId || !db) return;
        const fetchPlanner = async () => {
            try {
                let actualUid = plannerId;
                let data = null;

                let docSnap = await getDoc(doc(db, "users", plannerId));
                if (docSnap.exists()) {
                    data = docSnap.data();
                } else {
                    const q = query(collection(db, "users"), where("plannerWebsite.subdomain", "==", plannerId), limit(1));
                    const qSnap = await getDocs(q);
                    if (!qSnap.empty) {
                        data = qSnap.docs[0].data();
                        actualUid = qSnap.docs[0].id;
                    }
                }

                if (data) {
                    if (data.plannerWebsite && data.plannerWebsite.isPublished) {
                        setPlanner({ uid: actualUid, ...data });
                    } else {
                        setError("This portfolio is not currently published.");
                    }
                } else {
                    setError("Planner not found.");
                }
            } catch (err) {
                console.error(err);
                setError("An error occurred loading the portfolio.");
            } finally {
                setLoading(false);
            }
        };
        fetchPlanner();
    }, [plannerId, db]);

    useEffect(() => {
        if (planner?.plannerWebsite?.theme?.fontFamily) {
            const font = planner.plannerWebsite.theme.fontFamily.split(',')[0].replace(/'/g, "").replace(/ /g, "+");
            const linkId = "google-font-link-planner";
            let link = document.getElementById(linkId);
            if (!link) {
                link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                document.head.appendChild(link);
            }
            link.href = `https://fonts.googleapis.com/css?family=${font}:400,700&display=swap`;
        }
    }, [planner?.plannerWebsite?.theme?.fontFamily]);

    const handleInquire = async (pkgTitle) => {
        if (user && !user.isAnonymous) {
            try {
                const chatId = [user.uid, planner.uid].sort().join('_');
                const chatDocRef = doc(db, "chats", chatId);
                await setDoc(chatDocRef, {
                    participants: [user.uid, planner.uid],
                    participantEmails: [user.email, planner.email],
                    participantInfo: {
                        [user.uid]: { name: user.plannerName || user.firstName || user.email, email: user.email, profilePicUrl: user.profilePicUrl || null },
                        [planner.uid]: { name: planner.plannerName || planner.firstName || planner.email, email: planner.email, profilePicUrl: planner.profilePicUrl || null }
                    },
                    updatedAt: serverTimestamp(),
                }, { merge: true });
                
                const prefillMessage = `Hi! I'm interested in your ${pkgTitle ? `"${pkgTitle}" package` : 'services'}. Can we discuss details?`;
                navigateTo('messages', { chatId, prefillMessage });
            } catch (err) {
                console.error("Error starting chat:", err);
                alert("Failed to start chat. Please try again.");
            }
        } else {
            setSelectedPackage(pkgTitle);
            setShowLeadModal(true);
        }
    };

    const handleSubmitLead = async (e) => {
        e.preventDefault();
        setSubmittingLead(true);
        try {
            const leadData = {
                name: leadForm.name,
                email: leadForm.email,
                phone: leadForm.phone,
                eventDate: leadForm.eventDate,
                status: 'Lead',
                value: 0,
                notes: [{
                    text: `Website Inquiry!\nPackage of Interest: ${selectedPackage || 'General'}\n\nMessage:\n${leadForm.message}`,
                    date: new Date().toISOString()
                }],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            
            await addDoc(collection(db, `users/${planner.uid}/crm_clients`), leadData);
            setLeadSuccess(true);
            setTimeout(() => {
                setShowLeadModal(false);
                setLeadSuccess(false);
                setLeadForm({ name: '', email: '', phone: '', eventDate: '', message: '' });
            }, 3000);
        } catch (err) {
            console.error("Error submitting lead:", err);
            alert("Failed to send inquiry. Please try again.");
        } finally {
            setSubmittingLead(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div></div>;
    
    if (error) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
            <div className="text-4xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button onClick={() => navigateTo('home')} className="bg-pink-600 text-white px-6 py-2.5 rounded-full font-bold hover:bg-pink-700 transition">Return to Host-it!</button>
        </div>
    );

    return (
        <div className="min-h-screen bg-white">
            <SEO 
                title={`${planner.plannerName || planner.firstName || 'Planner'} Portfolio`} 
                description={planner.description || "Professional event planning services."} 
            />
            
            <div className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-200 z-[100] px-6 py-3 flex justify-between items-center shadow-sm">
                <div onClick={() => navigateTo('home')} className="cursor-pointer flex items-center gap-2">
                    <span className="font-bold text-pink-600 italic text-xl">Host-it!</span>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest hidden sm:inline border-l border-gray-300 pl-2">Pro Portfolios</span>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })} className="bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-black transition">
                        Contact Planner
                    </button>
                </div>
            </div>

            <div className="pt-14">
                <PlannerWebsiteRenderer 
                    sections={planner.plannerWebsite.sections} 
                    theme={planner.plannerWebsite.theme} 
                    planner={planner} 
                    onInquire={handleInquire}
                />
            </div>

            {showLeadModal && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-gray-50 p-6 border-b border-gray-100 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Get in Touch</h3>
                                <p className="text-sm text-gray-500 mt-1">Inquiring about: <strong className="text-pink-600">{selectedPackage || 'Planning Services'}</strong></p>
                            </div>
                            <button onClick={() => setShowLeadModal(false)} className="text-gray-400 hover:text-gray-800 text-3xl leading-none">&times;</button>
                        </div>
                        
                        <div className="p-6">
                            {leadSuccess ? (
                                <div className="text-center py-8">
                                    <div className="text-5xl mb-4">✨</div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">Inquiry Sent!</h4>
                                    <p className="text-gray-600">{planner.plannerName || 'The planner'} will be in touch with you shortly.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmitLead} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                            <input type="text" required value={leadForm.name} onChange={e => setLeadForm({...leadForm, name: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                                            <input type="email" required value={leadForm.email} onChange={e => setLeadForm({...leadForm, email: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                                            <PhoneInput value={leadForm.phone} onChange={e => setLeadForm({...leadForm, phone: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1">Event Date</label>
                                            <input type="date" value={leadForm.eventDate} onChange={e => setLeadForm({...leadForm, eventDate: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none bg-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                                        <textarea required value={leadForm.message} onChange={e => setLeadForm({...leadForm, message: e.target.value})} placeholder="Tell us a little about your vision..." rows="3" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none resize-none"></textarea>
                                    </div>
                                    
                                    <SmsComplianceCheckboxes />

                                    <div className="pt-4 border-t border-gray-100 flex justify-end gap-3 mt-2">
                                        <button type="button" onClick={() => setShowLeadModal(false)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                                        <button type="submit" disabled={submittingLead} className="px-6 py-2.5 bg-gray-900 text-white rounded-lg font-bold hover:bg-black transition shadow-md disabled:opacity-50">
                                            {submittingLead ? 'Sending...' : 'Send Inquiry'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <footer className="bg-gray-900 text-gray-400 py-8 text-center text-sm">
                <p>&copy; {new Date().getFullYear()} {planner.plannerName || planner.firstName}. All rights reserved.</p>
                <p className="mt-2">Created with <span className="text-pink-500 font-bold italic">Host-it! Pro</span></p>
            </footer>
        </div> 
    );
}
